

import {
  defineComponent, ref, onMounted, computed, watch, Ref, onUnmounted,
} from '@vue/composition-api';
import {
  FiveStars, FCButton, FCSelect,
} from '@fc/angie-ui';
import { useCartContext } from '~/composables/useCartContext';
import SplitView from '~/components/SplitView.vue';
import storeMutations from '~/store/constants/storeMutations';
import { useModal } from '~/composables/useModal';
import PdpCarousel from './PdpCarousel.vue';
import AffirmPackagePrice from './AffirmPackagePrice.vue';
import PackageIncludedCarousel from './PackageIncludedCarousel.vue';
import PdpSectionHeader from './PdpSectionHeader.vue';
import WrapGloveSize from './WrapGloveSize.vue';
import AtfWhatsIncluded from './AtfWhatsIncluded.vue';
import GenericUpsell from './GenericUpsell.vue';

interface ValidateFieldParams {
  hasError: boolean;
  errorField: Ref<boolean> | null;
  errorKey: string;
}

const data = [
  {
    title: 'Membership',
    copy: `
            <p>An active FightCamp Membership at $39/month is required.</p>
            <p>Membership gives you full access to our complete library of efficient, full-body workouts, and unlimited accounts for your household.</p>
            <p>Why you'll love the FightCamp Membership:</p>
            <ul>
                <li>3000+ on-demand boxing, kickboxing, strength, core & recovery workouts (more added weekly)</li>
                <li>World-class instructors</li>
                <li>Real-time progress tracking</li>
                <li>Programs with day-by-day workout plans, and more!</li>
            </ul>
        `,
  },
  {
    title: 'Console & Product Specs',
    copy: `
            <ul>
                <li>Console Dimensions: 7.9” x 3.9” x 1.3”</li>
                <li>Console Weight: 11 oz</li>
                <li>Water Protection: IP67 rated</li>
                <li>Mounting Options: Magnetic base plate with wall mounting, or place on flat surface</li>
                <li>Power Consumption: 10W</li>
                <li>USB-C Connector for Power: 5V DC @ 2A max</li>
                <li>Connectivity: Ethernet 10/100 Mbps</li>
                <li>Efficiency: Energy Star compliant</li>
                <li>Video Resolution: 1920-1080p at 60fps</li>
                <li>Tracker Battery Capacity: 25mAh</li>
                <li>Tracker Battery Full-Charging Time: 2hrs</li>
                <li>Tracker Battery Duration: 8hrs of workout time on full charge</li>
                <li>Wireless Range: 20-30 ft</li>
                <li>Memory: 4 GB RAM</li>
                <li>Storage: 16 GB internal flash storage</li>
                <li>WiFi 802.11 a/b/g/n/ac - 2.4GHz & 5GHz</li>
            </ul>
        `,
  },
  {
    title: 'Bag Specs',
    copy: `
            <ul>
                <li>Boxing Bag Height: 67" tall, when fully assembled</li>
                <li>Boxing Bag Top: 18" diameter</li>
                <li>Boxing Bag Material: Filled with high-density, durable foam, vinyl cover</li>
                <li>Boxing Bag Assembly: Hook-and-loop tension belts secure top of the bag to the base</li>
                <li>Boxing Bag Base: 24" diameter</li>
                <li>Boxing Bag Weight: Unfilled base (13lbs), Base filled with water (250lbs), Base filled with sand (350lbs), Bag top (17lbs), Fully assembled bag with sand (380lbs)</li>
                <li>Mobility: Tilt bag and roll to its desired location, even when fully filled & assembled</li>
            </ul>
        `,
  },
  {
    title: 'Warranty & 30-Day Refunds',
    copy: `
      <p class="fc-bold">Warranty<p>
      <p>Limited Warranty - 12-month plan included with your package, covers lost or damaged Trackers</p>

      <p class="fc-bold">30-day Refund Policy</p>
      <p>Try FightCamp at home for 30 days. If it’s not for you, we’ll give you a full refund.*</p>

      <p>*Terms and conditions apply. <a href="/return-policy">See Full Return Policy</a></p>
    `,
  },
  {
    title: 'Delivery & Setup',
    copy: `
      <p>Please note the boxing bag is shipped separately from the Console and accessories, and the boxing bag will arrive in 2 boxes: one for the base, one for the top.</p>

      <p>There may be a slight delay between the arrival of these separate shipments.</p>

      <p class="fc-bold">Setup</p>
      <p>Quickly connect your Console to your TV via HDMI, or connect to your mobile device via Bluetooth. Fill your boxing bag with sand or water, as preferred (sand not included), then simply attach the top to the base.</p>

      <a href="/how-it-works">Read More Setup Details</a>

      <p class="fc-bold">Setup Documents</p>
      <a href="https://joinfightcamp.com/bag-setup" target="_blank">Download Bag Setup Instructions</a>
      <br>
      <a href="https://joinfightcamp.com/console-setup" target="_blank">Download Console Setup Instructions</a>
    `,
  },
];

const errorMessage = 'Size is required before adding to cart.';

export default defineComponent({
  name: 'PackageAboveTheFold',
  components: {
    FiveStars,
    SplitView,
    PdpCarousel,
    AffirmPackagePrice,
    PackageIncludedCarousel,
    FCSelect,
    FCButton,
    PdpSectionHeader,
    WrapGloveSize,
    AtfWhatsIncluded,
    GenericUpsell,
  },
  props: {
    pdpUpsells: {
      type: Array as () => Array<{ id: string; label: string }>,
      required: true,
    },
    packageData: {
      type: Object,
      required: true,
    },
    popUpUpsells: {
      type: Array,
      required: true,
    },
  },
  setup(props) {
    const {
      $store, $ecommAnalytics, $analytics, $smoothScroll,
      // @ts-ignore
    } = useNuxtApp();

    const productsAdded = ref<string[]>([]);

    const { modalShow: openAddToCartSuccessModal } = useModal({
      component: () => import('~/components/checkout/addToCartSuccessModal.vue'),
      modalProps: {
        componentName: 'addToCartSuccessModal',
        adaptive: true,
        scrollable: true,
        width: '95%',
        maxWidth: 1000,
      },
      componentProps: {
        productsAdded,
        upsellProducts: props.popUpUpsells,
      },
      options: {
        closeCallback: () => { productsAdded.value = []; },
      },
    });

    // Add sticky button functionality
    const isButtonSticky = ref(false);
    let scrollTimeout: number | null = null;

    function handleScroll() {
      if (!process.client) return;

      if (scrollTimeout) {
        window.clearTimeout(scrollTimeout);
      }

      scrollTimeout = window.setTimeout(() => {
        const buttonContainer = document.querySelector('.pdp-above-the-fold__button-container');
        if (!buttonContainer) return;

        const buttonPosition = buttonContainer.getBoundingClientRect();
        isButtonSticky.value = buttonPosition.bottom < 0;
      }, 80);
    }

    onMounted(() => {
      $store.commit(storeMutations.SET_PACKAGE_PAGE_TOTAL, props.packageData?.price);
      window.addEventListener('scroll', handleScroll);
    });

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        window.clearTimeout(scrollTimeout);
      }
    });

    const productsCollection = computed(() => $store.getters.productsCollection);
    if (process.client) {
      watch(
        [productsCollection, () => props.packageData],
        () => {
          const shopifyProduct = productsCollection.value?.find((p: any) => p.id === props.packageData.id);
          if (shopifyProduct) {
            $ecommAnalytics.view_item([{
              item_id: shopifyProduct.id,
              item_name: shopifyProduct.title,
              price: shopifyProduct.variants[0].price,
              item_category: shopifyProduct.productType,
            }]);
            $analytics.productViewed(props.packageData);
          }
        },
        { immediate: true },
      );
    }

    const loading = ref(false);
    const { insertItemToCart } = useCartContext();
    const isError = ref(false);
    const errorsSelectIds = ref<string[]>([]);

    function validateField({ hasError, errorField, errorKey }: ValidateFieldParams) {
      if (hasError) {
        if (errorField) {
          errorField.value = true;
        }
        isError.value = true;
        errorsSelectIds.value.push(errorKey);
      } else {
        if (errorField) {
          errorField.value = false;
        }
        errorsSelectIds.value = errorsSelectIds.value.filter(id => id !== errorKey);
      }
    }

    const upsellErrorMap = computed(() => new Set(errorsSelectIds.value));

    async function addToCart() {
      loading.value = true;
      isError.value = false;
      errorsSelectIds.value = [];
      validateField({ errorKey: 'primaryQuickwrap', hasError: !sizeSelected.value, errorField: sizeSelectorError });

      // check for upsell size errors
      selectedUpsellVariants.value.forEach((product) => {
        if ((product.variants?.length ?? 0) > 1 && !product.selectedVariantId) {
          isError.value = true;
          errorsSelectIds.value.push(product.id);
        }
      });

      // if no error we know it is okay to add to cart and go to checkout
      if (!isError.value) {
        const itemsToInsertToCart: (string|null)[] = [sizeSelected.value ?? null];
        selectedUpsellVariants.value.forEach((product) => {
          itemsToInsertToCart.push(product.selectedVariantId);
        });

        const validItems = itemsToInsertToCart.filter((item): item is string => item !== null);
        await Promise.all(validItems.map((item) => insertItemToCart(item ?? '')));
        productsAdded.value.push(...validItems); // adding items selected to array to pass to the addToCartSuccessModal to decide which upsells to show
        openAddToCartSuccessModal();
      } else {
        // scroll to first error
        $smoothScroll({
          scrollTo: document.getElementById(errorsSelectIds.value[0]),
          offset: -100,
          updateHistory: false,
        });
      }
      loading.value = false;
    }

    function scrollToReviews() {
      $smoothScroll({
        scrollTo: document.getElementById('customerReviews'),
        offset: -100,
        updateHistory: false,
      });
    }

    // package size selector
    const sizeSelected = ref<string | undefined>('');
    const sizeSelectorError = ref(false);
    const sizeOptions = computed(() => (props.packageData?.variants ?? []).map((variant: any) => ({
      key: variant.id,
      name: variant.name,
    })));

    const selectedUpsellVariants = ref<any[]>([]);
    function handleUpsellSelection(payload: { checked: boolean; product: any }) {
      if (!payload.product) return;

      // Remove any previous selection for the same product id
      selectedUpsellVariants.value = selectedUpsellVariants.value.filter(
        (item) => item.id !== payload.product.id,
      );

      if (payload.checked) {
        selectedUpsellVariants.value.push(payload.product);
      }
      // No need to push if unchecked, as removal is already handled above
    }

    const totalPrice = ref(props.packageData?.price ?? 0);
    watch(selectedUpsellVariants, (selectedProducts) => {
      const basePrice = Number(props.packageData?.price ?? 0);
      let upsellPrice = 0;

      selectedProducts.forEach((product) => {
        upsellPrice += Number(product.variants[0].price ?? 0);
      });
      totalPrice.value = basePrice + upsellPrice;
    });

    const chooseText = computed(() => {
      if ($store.getters.currentRoute.name === 'shop-products-fightcamp-elite') {
        return 'Choose Your Glove & Wrap Size:';
      }
      return 'Choose Your Wrap Size:';
    });

    return {
      totalPrice,
      addToCart,
      loading,
      scrollToReviews,
      sizeSelected,
      sizeSelectorError,
      sizeOptions,
      includedData: computed(() => data),
      errorMessage,
      isButtonSticky,
      handleUpsellSelection,
      errorsSelectIds,
      upsellErrorMap,
      chooseText,
    };
  },
});
